import React, { useEffect, useState } from 'react';
import { ArrowLeft, Building2, CheckCircle, Database, Eye, EyeOff, Info, Key, Save, Server, Settings, ShieldCheck } from 'lucide-react';

const DEFAULT_CONFIG = {
  enabled: true,
  environment: 'sandbox-ocim',
  baseUrl: '',
  tokenUrl: '',
  authScheme: 'client_credentials',
  scope: 'urn:opc:hgbu:ws:__myscopes__',
  enterpriseId: 'OCR4ENT',
  chainCode: 'OHIPLAB',
  hotelId: 'OHIPSB02',
  hubId: '',
  externalSystemId: '',
  appKeySecretName: '',
  appKeySecretValue: '',
  clientIdSecretName: '',
  clientIdSecretValue: '',
  clientSecretSecretName: '',
  clientSecretSecretValue: '',
  accessTokenSecretName: '',
  accessTokenSecretValue: '',
  transactionPath: '',
  useDataApiFinancials: true,
  useAsyncDailySummary: true,
  useReservationSyncFallback: true,
  reservationStatusesText: 'Reserved\nConfirmed\nCheckedIn\nCheckedOut\nNoShow\nProspect',
  historicalDays: 31,
  forwardDays: 365
};

const MAX_HISTORICAL_DAYS = 365;
const MAX_FORWARD_DAYS = 730;

const defaultSecretName = (locationCode, suffix) => {
  const prefix = (locationCode || 'oracle-ohip')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'oracle-ohip';
  return `${prefix}-ohip-${suffix}`;
};

const OhipConfig = ({ selectedLocation, onSave, onBack, isLoading, error, initialConfig = null }) => {
  const [ohipConfig, setOhipConfig] = useState(DEFAULT_CONFIG);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showSecrets, setShowSecrets] = useState({
    appKey: false,
    clientId: false,
    clientSecret: false,
    accessToken: false
  });

  useEffect(() => {
    const locationCode = selectedLocation?.code || selectedLocation?.id || 'oracle-ohip';
    setOhipConfig(prev => ({
      ...prev,
      appKeySecretName: prev.appKeySecretName || defaultSecretName(locationCode, 'app-key'),
      clientIdSecretName: prev.clientIdSecretName || defaultSecretName(locationCode, 'client-id'),
      clientSecretSecretName: prev.clientSecretSecretName || defaultSecretName(locationCode, 'client-secret')
    }));
  }, [selectedLocation]);

  useEffect(() => {
    if (!initialConfig?.ohipConfig) return;
    const existing = initialConfig.ohipConfig;

    setOhipConfig({
      ...DEFAULT_CONFIG,
      ...existing,
      environment: existing.environment || 'custom',
      reservationStatusesText: Array.isArray(existing.reservationStatuses)
        ? existing.reservationStatuses.join('\n')
        : (existing.reservationStatuses || DEFAULT_CONFIG.reservationStatusesText)
    });
  }, [initialConfig]);

  const updateField = (field, value) => {
    setOhipConfig(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'environment' && value === 'sandbox-ocim') {
        next.authScheme = 'client_credentials';
        next.scope = 'urn:opc:hgbu:ws:__myscopes__';
        next.enterpriseId = next.enterpriseId || 'OCR4ENT';
        next.chainCode = next.chainCode || 'OHIPLAB';
        next.hotelId = next.hotelId || 'OHIPSB02';
      }
      return next;
    });
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateField = (field, value) => {
    let fieldError = null;
    const trimmed = typeof value === 'string' ? value.trim() : value;

    switch (field) {
      case 'baseUrl':
        if (!trimmed) fieldError = 'Gateway URL is required';
        else if (!/^https:\/\/[^ ]+$/.test(trimmed)) fieldError = 'Gateway URL must start with https://';
        break;
      case 'tokenUrl':
        if (trimmed && !/^https:\/\/[^ ]+$/.test(trimmed)) fieldError = 'Token URL must start with https://';
        break;
      case 'enterpriseId':
        if (!trimmed) fieldError = 'Enterprise ID is required for OCIM';
        break;
      case 'chainCode':
        if (!trimmed) fieldError = 'Chain code is required';
        break;
      case 'hotelId':
        if (!trimmed) fieldError = 'Oracle Hotel ID is required';
        break;
      case 'appKeySecretName':
      case 'clientIdSecretName':
      case 'clientSecretSecretName':
        if (!trimmed) fieldError = 'Key Vault secret name is required';
        else if (!/^[0-9a-zA-Z-]{1,127}$/.test(trimmed)) fieldError = 'Use only letters, numbers, and hyphens';
        break;
      case 'accessTokenSecretName':
        if (trimmed && !/^[0-9a-zA-Z-]{1,127}$/.test(trimmed)) fieldError = 'Use only letters, numbers, and hyphens';
        break;
      case 'scope':
        if (!trimmed) fieldError = 'OAuth scope is required';
        break;
      case 'historicalDays':
        if (!Number.isInteger(Number(value)) || Number(value) < 1 || Number(value) > MAX_HISTORICAL_DAYS) {
          fieldError = `Historical days must be 1-${MAX_HISTORICAL_DAYS}`;
        }
        break;
      case 'forwardDays':
        if (!Number.isInteger(Number(value)) || Number(value) < 1 || Number(value) > MAX_FORWARD_DAYS) {
          fieldError = `Forward days must be 1-${MAX_FORWARD_DAYS}`;
        }
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({ ...prev, [field]: fieldError }));
    return fieldError;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, ohipConfig[field]);
  };

  const validateAll = () => {
    const fields = [
      'baseUrl',
      'tokenUrl',
      'enterpriseId',
      'chainCode',
      'hotelId',
      'appKeySecretName',
      'clientIdSecretName',
      'clientSecretSecretName',
      'accessTokenSecretName',
      'scope',
      'historicalDays',
      'forwardDays'
    ];
    const errors = fields.reduce((acc, field) => ({ ...acc, [field]: validateField(field, ohipConfig[field]) }), {});
    setTouched(fields.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    return Object.values(errors).every(value => !value);
  };

  const reservationStatuses = ohipConfig.reservationStatusesText
    .split(/[\n,]/)
    .map(value => value.trim())
    .filter(Boolean);

  const handleSave = () => {
    if (!validateAll()) return;
    onSave({
      enabled: ohipConfig.enabled,
      environment: ohipConfig.environment,
      baseUrl: ohipConfig.baseUrl.trim(),
      ...(ohipConfig.tokenUrl.trim() ? { tokenUrl: ohipConfig.tokenUrl.trim() } : {}),
      authScheme: ohipConfig.authScheme,
      scope: ohipConfig.scope.trim(),
      enterpriseId: ohipConfig.enterpriseId.trim(),
      chainCode: ohipConfig.chainCode.trim(),
      hotelId: ohipConfig.hotelId.trim(),
      ...(ohipConfig.hubId.trim() ? { hubId: ohipConfig.hubId.trim() } : {}),
      ...(ohipConfig.externalSystemId.trim() ? { externalSystemId: ohipConfig.externalSystemId.trim() } : {}),
      appKeySecretName: ohipConfig.appKeySecretName.trim(),
      clientIdSecretName: ohipConfig.clientIdSecretName.trim(),
      clientSecretSecretName: ohipConfig.clientSecretSecretName.trim(),
      ...(ohipConfig.accessTokenSecretName.trim() ? { accessTokenSecretName: ohipConfig.accessTokenSecretName.trim() } : {}),
      ...(ohipConfig.appKeySecretValue.trim() ? { appKeySecretValue: ohipConfig.appKeySecretValue.trim() } : {}),
      ...(ohipConfig.clientIdSecretValue.trim() ? { clientIdSecretValue: ohipConfig.clientIdSecretValue.trim() } : {}),
      ...(ohipConfig.clientSecretSecretValue.trim() ? { clientSecretSecretValue: ohipConfig.clientSecretSecretValue.trim() } : {}),
      ...(ohipConfig.accessTokenSecretValue.trim() ? { accessTokenSecretValue: ohipConfig.accessTokenSecretValue.trim() } : {}),
      ...(ohipConfig.transactionPath.trim() ? { transactionPath: ohipConfig.transactionPath.trim() } : {}),
      useDataApiFinancials: Boolean(ohipConfig.useDataApiFinancials),
      useAsyncDailySummary: Boolean(ohipConfig.useAsyncDailySummary),
      useReservationSyncFallback: Boolean(ohipConfig.useReservationSyncFallback),
      reservationStatuses,
      historicalDays: Number(ohipConfig.historicalDays),
      forwardDays: Number(ohipConfig.forwardDays)
    });
  };

  const isFormValid = ohipConfig.baseUrl.trim()
    && ohipConfig.enterpriseId.trim()
    && ohipConfig.chainCode.trim()
    && ohipConfig.hotelId.trim()
    && ohipConfig.appKeySecretName.trim()
    && ohipConfig.clientIdSecretName.trim()
    && ohipConfig.clientSecretSecretName.trim()
    && ohipConfig.scope.trim()
    && !Object.values(validationErrors).some(Boolean);

  const inputClass = (field) => `cp-input${touched[field] && validationErrors[field] ? ' cp-invalid' : ''}`;

  const secretInput = (field, showKey, placeholder) => (
    <div className="relative mt-2">
      <input
        type={showSecrets[showKey] ? 'text' : 'password'}
        value={ohipConfig[field]}
        onChange={(event) => updateField(field, event.target.value)}
        placeholder={placeholder}
        autoComplete="new-password"
        className={`${inputClass(field)} pr-11`}
      />
      <button
        type="button"
        onClick={() => setShowSecrets(prev => ({ ...prev, [showKey]: !prev[showKey] }))}
        className="ep-reveal"
        aria-label={showSecrets[showKey] ? `Hide ${showKey}` : `Show ${showKey}`}
        title={showSecrets[showKey] ? `Hide ${showKey}` : `Show ${showKey}`}
      >
        {showSecrets[showKey] ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );

  return (
    <div className="ep-config-form">
      <div className="flex items-center space-x-2">
        <Database className="w-5 h-5 text-blue-600" />
        <h2 className="ep-config-title">Oracle OHIP PMS Integration</h2>
      </div>

      {selectedLocation && (
        <div className="ep-panel">
          <p className="cp-help">
            Configuring Oracle OHIP for: <strong>{selectedLocation.name}</strong> ({selectedLocation.code})
          </p>
        </div>
      )}

      <div className="ep-banner ep-tone-info">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="ep-banner-body">
            <p className="font-medium mb-1">OCIM client credentials with Key Vault secrets</p>
            <p>Store Oracle app key and OAuth credentials in Key Vault. Cosmos keeps only property identifiers, sync options, and secret names.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="ep-banner ep-tone-error">
          <p className="ep-banner-body">{error}</p>
        </div>
      )}

      <div className="ep-config-fields">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={ohipConfig.enabled}
            onChange={(event) => updateField('enabled', event.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div>
            <span className="cp-label">Enable Oracle OHIP sync</span>
            <p className="cp-help">Disabled configs stay saved but will not be used by the ETL.</p>
          </div>
        </label>

        <div className="ep-grid">
          <div>
            <label className="cp-label">
              <Server className="inline-block w-4 h-4 mr-1" />
              Environment
            </label>
            <select
              value={ohipConfig.environment}
              onChange={(event) => updateField('environment', event.target.value)}
              className="cp-input"
            >
              <option value="sandbox-ocim">Sandbox 1 OCIM</option>
              <option value="production-ocim">Production OCIM</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="cp-label">
              <ShieldCheck className="inline-block w-4 h-4 mr-1" />
              Auth Scheme
            </label>
            <select
              value={ohipConfig.authScheme}
              onChange={(event) => updateField('authScheme', event.target.value)}
              className="cp-input"
            >
              <option value="client_credentials">OCIM client credentials</option>
            </select>
          </div>
        </div>

        <div className="ep-grid">
          <div>
            <label className="cp-label">Gateway URL <span className="ep-req">*</span></label>
            <input
              type="text"
              value={ohipConfig.baseUrl}
              onChange={(event) => updateField('baseUrl', event.target.value)}
              onBlur={() => handleBlur('baseUrl')}
              placeholder="https://<ohip-gateway>"
              className={inputClass('baseUrl')}
            />
            {touched.baseUrl && validationErrors.baseUrl && <p className="cp-help cp-error">{validationErrors.baseUrl}</p>}
          </div>

          <div>
            <label className="cp-label">Token URL</label>
            <input
              type="text"
              value={ohipConfig.tokenUrl}
              onChange={(event) => updateField('tokenUrl', event.target.value)}
              onBlur={() => handleBlur('tokenUrl')}
              placeholder="Optional. Defaults to <Gateway URL>/oauth/v1/tokens"
              className={inputClass('tokenUrl')}
            />
            {touched.tokenUrl && validationErrors.tokenUrl && <p className="cp-help cp-error">{validationErrors.tokenUrl}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="cp-label">
              <Building2 className="inline-block w-4 h-4 mr-1" />
              Enterprise ID <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={ohipConfig.enterpriseId}
              onChange={(event) => updateField('enterpriseId', event.target.value)}
              onBlur={() => handleBlur('enterpriseId')}
              placeholder="OCR4ENT"
              className={inputClass('enterpriseId')}
            />
            {touched.enterpriseId && validationErrors.enterpriseId && <p className="cp-help cp-error">{validationErrors.enterpriseId}</p>}
          </div>

          <div>
            <label className="cp-label">Chain Code <span className="ep-req">*</span></label>
            <input
              type="text"
              value={ohipConfig.chainCode}
              onChange={(event) => updateField('chainCode', event.target.value)}
              onBlur={() => handleBlur('chainCode')}
              placeholder="OHIPLAB"
              className={inputClass('chainCode')}
            />
            {touched.chainCode && validationErrors.chainCode && <p className="cp-help cp-error">{validationErrors.chainCode}</p>}
          </div>

          <div>
            <label className="cp-label">Oracle Hotel ID <span className="ep-req">*</span></label>
            <input
              type="text"
              value={ohipConfig.hotelId}
              onChange={(event) => updateField('hotelId', event.target.value)}
              onBlur={() => handleBlur('hotelId')}
              placeholder="OHIPSB02"
              className={inputClass('hotelId')}
            />
            {touched.hotelId && validationErrors.hotelId && <p className="cp-help cp-error">{validationErrors.hotelId}</p>}
          </div>
        </div>

        <div className="ep-grid">
          <div>
            <label className="cp-label">Hub ID</label>
            <input
              type="text"
              value={ohipConfig.hubId}
              onChange={(event) => updateField('hubId', event.target.value)}
              placeholder="Optional for hub-level access"
              className={inputClass('hubId')}
            />
          </div>

          <div>
            <label className="cp-label">External System ID</label>
            <input
              type="text"
              value={ohipConfig.externalSystemId}
              onChange={(event) => updateField('externalSystemId', event.target.value)}
              placeholder="Optional. Enables async reservation daily summary."
              className={inputClass('externalSystemId')}
            />
          </div>
        </div>

        <div>
          <label className="cp-label">
            <Settings className="inline-block w-4 h-4 mr-1" />
            OAuth Scope <span className="ep-req">*</span>
          </label>
          <input
            type="text"
            value={ohipConfig.scope}
            onChange={(event) => updateField('scope', event.target.value)}
            onBlur={() => handleBlur('scope')}
            className={inputClass('scope')}
          />
          {touched.scope && validationErrors.scope && <p className="cp-help cp-error">{validationErrors.scope}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="cp-label">
              <Key className="inline-block w-4 h-4 mr-1" />
              App Key Secret Name <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={ohipConfig.appKeySecretName}
              onChange={(event) => updateField('appKeySecretName', event.target.value)}
              onBlur={() => handleBlur('appKeySecretName')}
              placeholder="westin-ohip-app-key"
              className={inputClass('appKeySecretName')}
            />
            {touched.appKeySecretName && validationErrors.appKeySecretName && <p className="cp-help cp-error">{validationErrors.appKeySecretName}</p>}
            {secretInput('appKeySecretValue', 'appKey', 'Optional. Paste Oracle application key value.')}
          </div>

          <div>
            <label className="cp-label">
              <Key className="inline-block w-4 h-4 mr-1" />
              Client ID Secret Name <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={ohipConfig.clientIdSecretName}
              onChange={(event) => updateField('clientIdSecretName', event.target.value)}
              onBlur={() => handleBlur('clientIdSecretName')}
              placeholder="westin-ohip-client-id"
              className={inputClass('clientIdSecretName')}
            />
            {touched.clientIdSecretName && validationErrors.clientIdSecretName && <p className="cp-help cp-error">{validationErrors.clientIdSecretName}</p>}
            {secretInput('clientIdSecretValue', 'clientId', 'Optional. Paste Oracle OAuth client id value.')}
          </div>

          <div>
            <label className="cp-label">
              <Key className="inline-block w-4 h-4 mr-1" />
              Client Secret Name <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={ohipConfig.clientSecretSecretName}
              onChange={(event) => updateField('clientSecretSecretName', event.target.value)}
              onBlur={() => handleBlur('clientSecretSecretName')}
              placeholder="westin-ohip-client-secret"
              className={inputClass('clientSecretSecretName')}
            />
            {touched.clientSecretSecretName && validationErrors.clientSecretSecretName && <p className="cp-help cp-error">{validationErrors.clientSecretSecretName}</p>}
            {secretInput('clientSecretSecretValue', 'clientSecret', 'Optional. Paste Oracle OAuth client secret value.')}
          </div>
        </div>

        <div>
          <label className="cp-label">Access Token Secret</label>
          <input
            type="text"
            value={ohipConfig.accessTokenSecretName}
            onChange={(event) => updateField('accessTokenSecretName', event.target.value)}
            onBlur={() => handleBlur('accessTokenSecretName')}
            placeholder="Optional. Use only for temporary supplied tokens."
            className={inputClass('accessTokenSecretName')}
          />
          {touched.accessTokenSecretName && validationErrors.accessTokenSecretName && <p className="cp-help cp-error">{validationErrors.accessTokenSecretName}</p>}
          {secretInput('accessTokenSecretValue', 'accessToken', 'Optional. Paste temporary access token to create/update secret.')}
        </div>

        <div className="ep-grid">
          <div>
            <label className="cp-label">Historical Days</label>
            <input
              type="number"
              min="1"
              max={MAX_HISTORICAL_DAYS}
              value={ohipConfig.historicalDays}
              onChange={(event) => updateField('historicalDays', event.target.value)}
              onBlur={() => handleBlur('historicalDays')}
              className={inputClass('historicalDays')}
            />
            {touched.historicalDays && validationErrors.historicalDays && <p className="cp-help cp-error">{validationErrors.historicalDays}</p>}
          </div>

          <div>
            <label className="cp-label">Forward Days</label>
            <input
              type="number"
              min="1"
              max={MAX_FORWARD_DAYS}
              value={ohipConfig.forwardDays}
              onChange={(event) => updateField('forwardDays', event.target.value)}
              onBlur={() => handleBlur('forwardDays')}
              className={inputClass('forwardDays')}
            />
            {touched.forwardDays && validationErrors.forwardDays && <p className="cp-help cp-error">{validationErrors.forwardDays}</p>}
          </div>
        </div>

        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={ohipConfig.useDataApiFinancials}
            onChange={(event) => updateField('useDataApiFinancials', event.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div>
            <span className="cp-label">Use OHIP Data API financial summary for posted revenue and journal</span>
            <p className="cp-help">Recommended for historical back-office revenue. Requires the Oracle Reporting & Analytics Data API subscription.</p>
          </div>
        </label>

        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={ohipConfig.useAsyncDailySummary}
            onChange={(event) => updateField('useAsyncDailySummary', event.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div>
            <span className="cp-label">Use asynchronous reservation daily summary when External System ID is available</span>
            <p className="cp-help">Recommended for OHIP revenue, tax, and reservation detail collection.</p>
          </div>
        </label>

        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={ohipConfig.useReservationSyncFallback}
            onChange={(event) => updateField('useReservationSyncFallback', event.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div>
            <span className="cp-label">Use synchronous reservations fallback</span>
            <p className="cp-help">Disable for sandbox tests when getReservations returns broad pages and INV/daily summary are the intended sources.</p>
          </div>
        </label>

        <div>
          <label className="cp-label">Reservation Statuses</label>
          <textarea
            value={ohipConfig.reservationStatusesText}
            onChange={(event) => updateField('reservationStatusesText', event.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
          />
          <p className="cp-help">Used by the synchronous reservations fallback. One status per line or comma separated.</p>
        </div>

        <div>
          <label className="cp-label">Financial Transaction Path</label>
          <input
            type="text"
            value={ohipConfig.transactionPath}
            onChange={(event) => updateField('transactionPath', event.target.value)}
            placeholder="Optional. Override if Oracle enables a financial transaction endpoint for this property."
            className={inputClass('transactionPath')}
          />
        </div>
      </div>

      {isFormValid && (
        <div className="ep-banner ep-tone-ok">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="ep-banner-body">Configuration is valid and ready to save</p>
          </div>
        </div>
      )}

      <div className="ep-config-foot">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="cp-btn"
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Setup
        </button>

        <button
          onClick={handleSave}
          disabled={isLoading || !isFormValid}
          className="cp-btn cp-primary"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={16} />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OhipConfig;
